<template>
  <div class="concepts-search-container">
    <h1>{{ $t('concepts.search.title') }}</h1>
    <div class="search-bar">
      <div class="search-input">
        <input v-model="queryText" />
      </div>
    </div>
    <div>
      <div v-if="concepts.length">
        <div class="total-text">
          {{
            $tc('concepts.search.results', total.value, {
              showCount: concepts.length,
              count: total.value,
            })
          }}
        </div>
        <div v-bind:key="concept.id" v-for="concept in concepts" class="card">
          <div class="card-number">
            <span>{{ assignedOnNumber(concept) }}</span>
          </div>
          <div class="card-body">
            <div class="card-title">{{ resolveField(concept, 'label') }}</div>
            <div class="card-description">
              {{ resolveField(concept, 'body') }}
            </div>
          </div>
          <div class="card-numbers-wrapper">
            <div class="validated-number">
              <font-awesome-icon
                slot="prefix"
                class="select-icon"
                :icon="['fa', 'thumbs-up']"
              />
              <span>{{ userAssignedOnNumber(concept) }}</span>
            </div>
            <div class="rejected-number">
              <font-awesome-icon
                slot="prefix"
                class="select-icon"
                :icon="['fa', 'thumbs-down']"
              />
              <span>{{ userRejectedOnNumber(concept) }}</span>
            </div>
          </div>
        </div>
      </div>
      <div v-if="canLoadMore" class="load-more">
        <button @click="loadMore">
          {{ loadingMore ? $t('concepts.search.loading') : $t('concepts.how-more') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex';


export default {
  name: 'ConceptSearch',
  data() {
    return {
      queryText: '',
      timeout: null,
      concepts: [],
      total: null,
      loadingMore: false,
    };
  },
  mounted() {
    this.debouncedSearch();
  },
  computed: {
    hasMoreConcepts() {
      return this.concepts.length < this.total.value;
    },
    canLoadMore() {
      return (
        this.total &&
        this.concepts &&
        !this.loadingMore &&
        this.concepts.length < this.total.value
      );
    },
  },
  methods: {
    ...mapActions('knowledgeModule', ['searchConcepts']),
    assignedOnNumber(concept) {
      return concept.concepts ? concept.concepts.nAssignedOn || 0 : 0;
    },
    userAssignedOnNumber(concept) {
      return concept.concepts ? concept.concepts.nUserAssignedOn || 0 : 0;
    },
    userRejectedOnNumber(concept) {
      return concept.concepts ? concept.concepts.nUserRejectedOn || 0 : 0;
    },
    resolveField(concept, field) {
      if (
        concept.defaultLanguage &&
        concept.translations[concept.defaultLanguage] &&
        concept.translations[concept.defaultLanguage][field]
      ) {
        return concept.translations[concept.defaultLanguage][field];
      }
      for (let lang of Object.keys(concept.translations)) {
        if (concept.translations[lang][field]) {
          return concept.translations[lang][field];
        }
      }
      return null;
    },
    debouncedSearch() {
      if (this.timeout) clearTimeout(this.timeout);
      this.timeout = setTimeout(this.runSearch, 150);
    },
    async runSearch() {
      clearTimeout(this.timeout);
      this.timeout = null;
      const result = await this.searchConcepts({
        queryText: this.queryText,
      });
      this.concepts = result.concepts;
      this.total = result.total;
      this.searchAfter = result.search_after;
    },
    async loadMore() {
      if (!this.canLoadMore) this.loadingMore = true;
      const result = await this.searchConcepts({
        queryText: this.queryText,
        searchAfter: this.searchAfter,
      });
      this.concepts = [...this.concepts, ...result.concepts];
      this.searchAfter = result.search_after;
      this.loadingMore = false;
    },
  },
  watch: {
    async queryText() {
      this.debouncedSearch();
    },
  },
};
</script>

<style scoped lang="scss">
.concepts-search-container {
  padding: 16px;
}

.card {
  border: 1px solid #ccc;
  border-radius: 4px;
  padding: 10px;
  margin-bottom: 10px;

  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-direction: row;
  gap: 10px;
}

.load-more {
  border: 1px solid #ccc;
  border-radius: 4px;
  padding: 10px;
  margin-bottom: 10px;
}
.card-number {
  min-width: 30px;
}

.card-numbers-wrapper {
  display: flex;
  gap: 20px;
}

.card-body {
  flex: 2;
  display: flex;
  flex-direction: column;
  padding: 0px;
}

.card-title {
  text-align: left;
  text-transform: uppercase;
  font-weight: 700;
  margin-bottom: 0px;
}

.card-description {
  font-size: 0.8em;
  color: darkgray;
}

.validated-number {
  color: $green-5-mayday;
  display: flex;
  gap: 5px;
}

.rejected-number {
  color: $red-5-mayday;
  display: flex;
  gap: 5px;
}

.search-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-direction: row;
  gap: 10px;
}

.search-input {
  flex: 1;
}

.search-input input {
  width: 100%;
}

.total-text {
  font-size: 0.8em;
  color: darkgray;
}
</style>
